import { useEffect, useId, useMemo, useState } from "react";
import { Input, Modal } from "@iq/models";
import { createChangeFromProblem, getChanges, linkProblemToChange } from "../../services/ticket_service";
import "./LinkChangeModal.css";

const MODE_OPTIONS = [
    { value: "link_existing", label: "Link Existing" },
    { value: "create_new", label: "Create New" },
];

const OPEN_CHANGE_STATUSES = new Set(["new", "assessment", "approved", "scheduled", "implementing", "failed"]);
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const RISK_OPTIONS = ["low", "medium", "high"];
const APPROVAL_OPTIONS = ["pending", "approved", "rejected", "waived"];

function formatChangeLabel(change) {
    const number = change?.number || `CHG-${change?.id ?? "?"}`;
    const title = change?.title || "Untitled Change";
    return `${number} - ${title}`;
}

function buildAssigneeLabel(member) {
    return member?.name || member?.email || `User #${member?.id ?? "?"}`;
}

export default function LinkChangeModal({
    isOpen,
    onClose,
    problem,
    teamMembers = [],
    onCompleted,
}) {
    const formId = useId();
    const [mode, setMode] = useState("link_existing");
    const [selectedChangeId, setSelectedChangeId] = useState("");
    const [loadingChanges, setLoadingChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [changeSearch, setChangeSearch] = useState("");
    const [changes, setChanges] = useState([]);
    const [formData, setFormData] = useState({
        assigned_to_id: problem?.assigned_to_id != null ? String(problem.assigned_to_id) : "",
        title: problem?.title || "",
        description: problem?.description || "",
        priority: problem?.priority || "medium",
        risk: "medium",
        change_type: "",
        approval_status: "pending",
        rollback_plan: "",
    });

    const assigneeOptions = useMemo(
        () =>
            (Array.isArray(teamMembers) ? teamMembers : [])
                .map((member) => ({ value: String(member?.id ?? ""), label: buildAssigneeLabel(member) }))
                .filter((option) => option.value !== ""),
        [teamMembers]
    );

    const filteredOpenChanges = useMemo(() => {
        const search = changeSearch.trim().toLowerCase();

        return (Array.isArray(changes) ? changes : [])
            .filter((change) => OPEN_CHANGE_STATUSES.has(String(change?.status || "").toLowerCase()))
            .filter((change) => {
                if (!search) {
                    return true;
                }

                return [change?.number, change?.title, change?.description]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(search));
            })
            .sort((left, right) => new Date(right?.created_at || 0) - new Date(left?.created_at || 0));
    }, [changeSearch, changes]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setMode("link_existing");
        setSelectedChangeId("");
        setChangeSearch("");
        setError("");
        setSaving(false);
        setLoadingChanges(true);
        setFormData({
            assigned_to_id: problem?.assigned_to_id != null ? String(problem.assigned_to_id) : "",
            title: problem?.title || "",
            description: problem?.description || "",
            priority: problem?.priority || "medium",
            risk: "medium",
            change_type: "",
            approval_status: "pending",
            rollback_plan: "",
        });

        let cancelled = false;

        (async () => {
            try {
                const loadedChanges = await getChanges({ limit: 200 });
                if (!cancelled) {
                    setChanges(Array.isArray(loadedChanges) ? loadedChanges : []);
                }
            } catch (changeError) {
                if (!cancelled) {
                    setChanges([]);
                    setError(changeError?.message || "Failed to load changes");
                }
            } finally {
                if (!cancelled) {
                    setLoadingChanges(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, problem]);

    const selectedChange = useMemo(
        () => filteredOpenChanges.find((change) => String(change?.id) === String(selectedChangeId)),
        [filteredOpenChanges, selectedChangeId]
    );

    const canSubmit = useMemo(() => {
        if (saving || !problem?.id) {
            return false;
        }

        if (mode === "link_existing") {
            return Boolean(selectedChangeId);
        }

        return Boolean(formData.risk);
    }, [formData.risk, mode, problem?.id, saving, selectedChangeId]);

    const actionButtons = useMemo(
        () => [
            {
                key: "cancel-link-change",
                text: "Cancel",
                color: "light",
                onClick: onClose,
                disabled: saving,
            },
            {
                key: "submit-link-change",
                text: saving
                    ? mode === "link_existing"
                        ? "Linking..."
                        : "Creating..."
                    : mode === "link_existing"
                        ? "Link Change"
                        : "Create Change",
                color: "accent",
                type: "submit",
                form: formId,
                disabled: !canSubmit,
            },
        ],
        [canSubmit, formId, mode, onClose, saving]
    );

    function handleFieldChange(event) {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError("");
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!problem?.id) {
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (mode === "link_existing") {
                const updatedProblem = await linkProblemToChange(problem.id, Number(selectedChangeId));
                onCompleted?.({
                    mode,
                    problem: updatedProblem,
                    changeId: Number(selectedChangeId),
                });
                onClose?.();
                return;
            }

            const payload = {
                assigned_to_id: formData.assigned_to_id ? Number(formData.assigned_to_id) : null,
                title: formData.title.trim(),
                description: formData.description.trim(),
                priority: formData.priority || "medium",
                risk: formData.risk || "medium",
                change_type: formData.change_type.trim(),
                approval_status: formData.approval_status || "pending",
                rollback_plan: formData.rollback_plan.trim(),
            };

            const createdPayload = await createChangeFromProblem(problem.id, payload);
            onCompleted?.({
                mode,
                problem: createdPayload?.problem,
                change: createdPayload?.change,
            });
            onClose?.();
        } catch (submitError) {
            setError(submitError?.message || "Failed to link change");
        } finally {
            setSaving(false);
        }
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Link Change"
            actionButtons={actionButtons}
            errorMessage={error}
            tall
        >
            <form id={formId} onSubmit={handleSubmit} className="link-change-modal-form">
                <div className="link-change-mode-selector">
                    {MODE_OPTIONS.map((option) => (
                        <div
                            key={option.value}
                            className={`link-change-mode-selector-option ${mode === option.value ? "link-change-mode-selector-option-active" : ""}`}
                            onClick={() => {
                                setMode(option.value);
                                setError("");
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>

                {mode === "link_existing" ? (
                    <div className="link-change-existing-panel">
                        <div>
                            <label htmlFor="change-search">Search Changes</label>
                            <Input
                                id="change-search"
                                type="search"
                                name="change-search"
                                value={changeSearch}
                                onChange={(event) => setChangeSearch(event.target.value)}
                                placeholder="Search by change number, title, or description"
                                disabled={loadingChanges || mode !== "link_existing"}
                                colorScheme="light"
                            />
                        </div>

                        <div className="link-change-panel-title-row">
                            <h3>Open Changes</h3>
                            <span>{filteredOpenChanges.length} found</span>
                        </div>

                        <div className="link-change-list" role="listbox" aria-label="Open changes">
                            {loadingChanges ? (
                                <div className="modal-empty-state">Loading changes...</div>
                            ) : filteredOpenChanges.length === 0 ? (
                                <div className="modal-empty-state">No open changes found.</div>
                            ) : (
                                filteredOpenChanges.map((change) => {
                                    const isSelected = String(selectedChangeId) === String(change?.id);

                                    return (
                                        <button
                                            key={change.id}
                                            type="button"
                                            className={[
                                                "link-change-list-item",
                                                isSelected ? "link-change-list-item--selected" : "",
                                            ].filter(Boolean).join(" ")}
                                            onClick={() => {
                                                setSelectedChangeId(String(change.id));
                                                setError("");
                                            }}
                                        >
                                            <div className="link-change-list-item__headline">{change?.title}</div>
                                            <div className="link-change-list-item__meta">{change.number}</div>
                                            <div className="link-change-list-item__meta">
                                                <span>Status: {change?.status || "-"}</span>
                                                <span>Priority: {change?.priority || "-"}</span>
                                                <span>Risk: {change?.risk || "-"}</span>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {selectedChange ? (
                            <div className="link-change-selected-summary">
                                Selected: {formatChangeLabel(selectedChange)}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="form-fields-container link-change-create-grid">
                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="link-change-assigned-to">Assigned To</label>
                            <Input
                                id="link-change-assigned-to"
                                type="dropdown"
                                value={assigneeOptions.find((option) => String(option.value) === String(formData.assigned_to_id))?.label || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={assigneeOptions}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, assigned_to_id: String(option.value) }))}
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="link-change-title">Title</label>
                            <Input
                                id="link-change-title"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleFieldChange}
                                placeholder="Defaults to problem title when left blank"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="link-change-description">Description</label>
                            <Input
                                id="link-change-description"
                                type="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleFieldChange}
                                placeholder="Defaults to problem description when left blank"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="link-change-priority">Priority</label>
                            <Input
                                id="link-change-priority"
                                type="dropdown"
                                value={PRIORITY_OPTIONS.find((option) => option === formData.priority)?.toString() || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={PRIORITY_OPTIONS.map((option) => ({ value: option, label: option }))}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, priority: option.value }))}
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="link-change-risk">Risk</label>
                            <Input
                                id="link-change-risk"
                                type="dropdown"
                                value={RISK_OPTIONS.find((option) => option === formData.risk)?.toString() || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={RISK_OPTIONS.map((option) => ({ value: option, label: option }))}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, risk: option.value }))}
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="link-change-approval-status">Approval</label>
                            <Input
                                id="link-change-approval-status"
                                type="dropdown"
                                value={APPROVAL_OPTIONS.find((option) => option === formData.approval_status)?.toString() || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={APPROVAL_OPTIONS.map((option) => ({ value: option, label: option }))}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, approval_status: option.value }))}
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="link-change-type">Change Type</label>
                            <Input
                                id="link-change-type"
                                type="text"
                                name="change_type"
                                value={formData.change_type}
                                onChange={handleFieldChange}
                                placeholder="standard | emergency | normal"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="link-change-rollback-plan">Rollback Plan</label>
                            <Input
                                id="link-change-rollback-plan"
                                type="textarea"
                                name="rollback_plan"
                                value={formData.rollback_plan}
                                onChange={handleFieldChange}
                                placeholder="Rollback plan"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}
